import { FC } from "preact/compat";
import { useEffect } from "preact/hooks";
import { useComputed, useSignal } from "@preact/signals";
import { addMonths, format, isSameMonth, isSameYear } from "date-fns";
import { useApp } from "~/core";
import { IconButton, SlideText } from "~/components";
import { useAppTime } from "~/hooks";
import { HintMessage } from "~/organisms";
import { WEEKDAY_SHORT_NAMES, getMonthGrid } from "~/utils";
import { useMobileDashboard } from "../../context";
import { getDateKey } from "../../utils";
import { SingleWidgetProps } from "../interfaces";
import GridCell from "./GridCell";
import Popup from "./Popup";
import PrevIcon from "@material-design-icons/svg/outlined/chevron_left.svg";
import NextIcon from "@material-design-icons/svg/outlined/chevron_right.svg";
import TimeIcon from "@material-design-icons/svg/outlined/schedule.svg";

const Widget: FC<SingleWidgetProps> = ({ className }) => {
    const { mobile } = useApp();
    const {
        mode,
        selectedDate,
        selectedDay,
        selectedProjectId,
        selectedFeeCodeId,
    } = useMobileDashboard();
    const today = useAppTime("day");
    const grid = useComputed(() => getMonthGrid(selectedDate.value));
    const popupDate = useSignal<Date | null>(null);
    const popupPosition = useSignal({ x: 0, y: 0 });
    const isPopup = useSignal(false);

    useEffect(() => {
        const checkPopup = (e: TouchEvent) => {
            if (!isPopup.peek()) {
                return;
            }

            const target =
                e.target instanceof Element
                    ? e.target.closest("[data-cell-popup],[data-grid-cell]")
                    : null;

            if (!target) {
                isPopup.value = false;
                mode.value = "month";
                mobile.impact("light");
            }
        };

        const handleTouchEnd = (e: TouchEvent) => {
            checkPopup(e);

            if (mode.peek() !== "day") {
                return;
            }

            const target =
                e.target instanceof Element
                    ? e.target.closest("button,label,[data-cell-popup]")
                    : null;

            if (!target) {
                mode.value = "month";
                mobile.impact("light");
            }
        };

        window.addEventListener("touchend", handleTouchEnd);
        window.addEventListener("touchcancel", checkPopup);
        window.addEventListener("touchmove", checkPopup);

        return () => {
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", checkPopup);
            window.removeEventListener("touchmove", checkPopup);
        };
    }, [
        mode,
        mobile,
        isPopup,
        selectedDay,
        selectedProjectId,
        selectedFeeCodeId,
    ]);

    const handleMoveBack = () => {
        const now = new Date();

        if (isSameMonth(now, selectedDate.peek())) {
            return;
        }

        selectedDate.value = now;
        mode.value = "month";
    };

    const handleMove = (shift: number) => {
        selectedDate.value = addMonths(selectedDate.peek(), shift);
        mode.value = "month";
        mobile.impact("light");
    };

    const handlePrev = () => handleMove(-1);

    const handleNext = () => handleMove(1);

    return (
        <div className={className}>
            <div className="flex justify-between gap-x-2 items-center px-2 py-1 border-b border-gray-200">
                <div className="flex items-center gap-x-2 text-gray-500 font-bold px-2 py-1">
                    <TimeIcon width="20" height="20" className="fill-current" />
                    <span>Time</span>
                </div>
                <div className="flex gap-x-2 items-center">
                    <IconButton
                        title="Previous month"
                        onClick={handlePrev}
                        className="p-1 flex-shrink-0"
                        flat
                    >
                        <PrevIcon width="18" height="18" />
                    </IconButton>
                    <div className="text-center w-[140px]">
                        <SlideText
                            className="w-full cursor-pointer"
                            onClick={handleMoveBack}
                        >
                            {isSameYear(selectedDate.value, today.value)
                                ? format(selectedDate.value, "MMMM")
                                : format(selectedDate.value, "MMMM, yyyy")}
                        </SlideText>
                    </div>
                    <IconButton
                        title="Next month"
                        onClick={handleNext}
                        className="p-1 flex-shrink-0"
                        flat
                    >
                        <NextIcon width="18" height="18" />
                    </IconButton>
                </div>
            </div>
            <div className="grid grid-cols-7 gap-2 p-2 callout-none">
                {grid.value.map((day) => (
                    <GridCell
                        key={getDateKey(day, selectedDate.value)}
                        isPopup={isPopup}
                        onLongPress={(coords) => {
                            const wasOn = isPopup.peek();

                            popupPosition.value = coords;
                            popupDate.value = day;
                            isPopup.value = true;

                            if (
                                !selectedProjectId.peek() &&
                                !selectedFeeCodeId.peek()
                            ) {
                                selectedDay.value = day;
                                mode.value = "day";
                            }

                            if (!wasOn) {
                                mobile.impact("medium");
                            }
                        }}
                        date={day}
                    />
                ))}
            </div>
            <div className="w-full grid grid-cols-7 gap-2 border-t border-gray-200 px-2 py-1 min-h-[34px]">
                {WEEKDAY_SHORT_NAMES.map((day) => (
                    <div
                        key={day}
                        className="text-center font-bold text-gray-500"
                    >
                        {day}
                    </div>
                ))}
            </div>
            {mode.value === "day" && (
                <HintMessage
                    storageKey="v1_m-dashboard_lp-hint-seen"
                    className="absolute left-1 right-1 bottom-1"
                >
                    Long press on a day to see the details
                </HintMessage>
            )}
            <Popup
                show={isPopup}
                coords={popupPosition}
                date={popupDate.value}
                onClose={() => {
                    isPopup.value = false;
                    mode.value = "month";
                    mobile.impact("light");
                }}
            />
        </div>
    );
};

export default Widget;
